export function prettyDateLabel(value: any): string | null {
  if (value == null) return null;
  let d: Date | null = null;
  if (value instanceof Date) d = value;
  else if (typeof value === "number") {
    d = value > 1e12 ? new Date(value) : new Date(value * 1000);
  } else if (typeof value === "string") {
    const trimmed = value.trim();
    const n = Number(trimmed);
    if (!Number.isNaN(n) && trimmed !== "") {
      d = n > 1e12 ? new Date(n) : new Date(n * 1000);
    } else {
      const parsed = Date.parse(trimmed);
      if (!Number.isNaN(parsed)) d = new Date(parsed);
    }
  }
  return d && !Number.isNaN(d.getTime())
    ? d
        .toLocaleString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "numeric",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        })
        .replace(/\s?(AM|PM)\b/g, (m) => m.toLowerCase())
    : null;
}

export function parseResidentKeyFromSeed(seedJson: any): string | null {
  try {
    const seed =
      typeof seedJson === "string" ? JSON.parse(seedJson) : seedJson;
    if (!seed || typeof seed !== "object") return null;
    const anySeed = seed as Record<string, any>;
    const rawExtra = anySeed.extra;
    const extra =
      rawExtra && typeof rawExtra === "string"
        ? JSON.parse(rawExtra)
        : (rawExtra as Record<string, any> | undefined) || {};
    const key =
      (anySeed.residentKey as string) ||
      (anySeed.residentInitials as string) ||
      (extra.patientKey as string) ||
      (extra.patientInitials as string) ||
      null;
    const normalized = String(key || "").trim();
    return normalized || null;
  } catch {
    return null;
  }
}

export function parseExtraLoose(raw: any): any | null {
  if (raw == null) return null;
  if (typeof raw === "object" && !Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const once = JSON.parse(raw);
      if (typeof once === "string") {
        try {
          return JSON.parse(once);
        } catch {
          return once;
        }
      }
      return once;
    } catch {
      return null;
    }
  }
  try {
    return JSON.parse(String(raw));
  } catch {
    return null;
  }
}

export function getConciergeMetaFromLead(lead: any): {
  isConcierge: boolean;
  isRush: boolean;
  agent?: { name?: string; email?: string; phone?: string } | null;
} {
  const ex = parseExtraLoose(lead?.extra);
  const mode = ex?.pricing?.mode ?? null;
  const leadAgent =
    lead?.assignedAgent ||
    lead?.sourceAgent ||
    lead?.commissionAgent ||
    null;
  const rawAgent =
    ex?.conciergeAgent ||
    ex?.agent ||
    ex?.adminContact ||
    leadAgent ||
    null;
  const isConcierge = mode === "CONCIERGE" || !!rawAgent;
  const isRush = !!ex?.pricing?.isRush;
  const agent = rawAgent
    ? {
        name:
          rawAgent?.name ??
          rawAgent?.username ??
          null,
        email: rawAgent?.email ?? null,
        phone: rawAgent?.phone ?? null,
      }
    : null;
  return { isConcierge, isRush, agent };
}

export function parseBroadcastSeed(seedJson: any): {
  residentInitials?: string | null;
  careNeed?: string | null;
  payerType?: string | null;
  notes?: string | null;
  urgency?: string | null;
  referrerStatusMessage?: string | null;
  referrerContact?: { name?: string; email?: string; phone?: string } | null;
} {
  try {
    const seed = typeof seedJson === "string" ? JSON.parse(seedJson) : seedJson;
    if (!seed || typeof seed !== "object") return {};
    const anySeed = seed as Record<string, any>;
    const rawExtra = anySeed.extra;
    const extra =
      rawExtra && typeof rawExtra === "string"
        ? JSON.parse(rawExtra)
        : (rawExtra as Record<string, any> | undefined) || {};

    return {
      residentInitials:
        (anySeed.residentKey as string) ||
        (anySeed.residentInitials as string) ||
        (extra.patientInitials as string) ||
        null,
      careNeed:
        (anySeed.careLevelRequested as string) ||
        (anySeed.careNeed as string) ||
        (extra.careNeed as string) ||
        null,
      payerType:
        (anySeed.payerType as string) || (extra.payerType as string) || null,
      notes:
        (anySeed.intakeNotes as string) ||
        (anySeed.notes as string) ||
        (extra.notes as string) ||
        null,
      urgency: (anySeed.urgency as string) || null,
      referrerStatusMessage: (extra.referrerStatusMessage as string) || null,
      referrerContact: (extra.referrerContact as
        | { name?: string; email?: string; phone?: string }
        | null) as any,
    };
  } catch {
    return {};
  }
}

export function getLeadBroadcastGroupId(lead: any): string | null {
  const extra = parseExtraLoose(lead?.extra);
  const directGroupId = lead?.group?.id ?? lead?.groupId ?? null;
  const broadcastGroupId = extra?.broadcast?.groupId ?? null;
  const normalized = directGroupId ?? broadcastGroupId;
  return normalized == null ? null : String(normalized);
}

export function hasReferrerCommunicationStarted(lead: any): boolean {
  const status = String(lead?.status || "").toUpperCase();
  return [
    "QUALIFYING",
    "MATCHING",
    "TOUR_SCHEDULED",
    "PENDING_DECISION",
    "PLACED",
    "CLOSED",
  ].includes(status);
}

export function getTourScheduleFromLead(lead: any): {
  id?: string | number | null;
  bookingTime?: string | null;
  businessName?: string | null;
} | null {
  const status = String(lead?.status || "").toUpperCase();
  const extra = parseExtraLoose(lead?.extra) || {};
  const tourSchedule = extra?.tourSchedule;
  const bookingTime = String(tourSchedule?.bookingTime || "").trim();
  if (!bookingTime && status !== "TOUR_SCHEDULED") return null;
  return {
    id:
      String(
        tourSchedule?.bookingId || tourSchedule?.conciergeMirrorBookingId || "",
      ).trim() || null,
    bookingTime: bookingTime || null,
    businessName:
      String(tourSchedule?.businessName || lead?.business?.name || "").trim() ||
      null,
  };
}

export function formatTourScheduleLabel(
  bookingTime?: string | null,
): string | null {
  if (!bookingTime) return null;
  const trimmed = String(bookingTime).trim();
  const numeric = Number(trimmed);
  const dt =
    !Number.isNaN(numeric) && trimmed !== ""
      ? new Date(numeric > 1e12 ? numeric : numeric * 1000)
      : new Date(trimmed);
  if (Number.isNaN(dt.getTime())) return null;
  return dt
    .toLocaleString(undefined, {
      weekday: "long",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .replace(/\s?(AM|PM)\b/g, (m) => m.toLowerCase());
}

export function getBookedTourMessage(
  lead: any,
  bookedTour: { bookingTime?: string | null; businessName?: string | null },
): string {
  const providerName =
    bookedTour?.businessName || lead?.business?.name || "the provider";
  const whenLabel = formatTourScheduleLabel(bookedTour?.bookingTime);
  if (whenLabel) {
    return `You have been scheduled for ${whenLabel} with ${providerName}.`;
  }
  return `Your tour has been scheduled with ${providerName}.`;
}

export function findScheduledTourBookingForLead(
  lead: any,
  bookings: any[],
): {
  id?: string | number | null;
  bookingTime?: string | null;
  businessName?: string | null;
} | null {
  if (!Array.isArray(bookings) || bookings.length === 0) return null;
  const leadId = String(lead?.id || "").trim();
  const extra = parseExtraLoose(lead?.extra) || {};
  const scheduledBookingId = String(extra?.tourSchedule?.bookingId || "").trim();
  const conciergeMirrorBookingId = String(
    extra?.tourSchedule?.conciergeMirrorBookingId || ""
  ).trim();
  const providerName =
    String(extra?.tourSchedule?.businessName || lead?.business?.name || "").trim() ||
    null;
  if (!leadId && !scheduledBookingId && !conciergeMirrorBookingId) return null;
  const now = Date.now();
  const matchesStoredBooking = (booking: any) => {
    const bookingId = String(booking?.id || "").trim();
    const externalBookingId = String(booking?.bookingId || "").trim();
    return (
      (!!scheduledBookingId && bookingId === scheduledBookingId) ||
      (!!conciergeMirrorBookingId && bookingId === conciergeMirrorBookingId) ||
      (!!scheduledBookingId && externalBookingId === `coord:${scheduledBookingId}`)
    );
  };
  const activeBookings = bookings.filter((booking: any) => {
    if (Number(booking?.status) === 0) return false;
    const whenLabel = formatTourScheduleLabel(String(booking?.time || "").trim());
    if (!whenLabel) return false;
    const when = Date.parse(String(booking?.time || "").trim());
    if (!Number.isFinite(when)) return false;
    return when >= now - 24 * 60 * 60 * 1000;
  });
  const storedMatch =
    scheduledBookingId || conciergeMirrorBookingId
      ? activeBookings.find((booking: any) => matchesStoredBooking(booking)) || null
      : null;
  const leadMatch = activeBookings.find((booking: any) => {
    const referralLeadId = String(booking?.referralLeadId || "").trim();
    if (referralLeadId === leadId) {
      const when = formatTourScheduleLabel(String(booking?.time || "").trim());
      return !!when;
    }
    const notes = String(booking?.quickNotes || "").trim();
    const leadMatch = notes.match(/(?:referral|tour\s+for)\s+lead\s*#(\d+)/i);
    return !!leadMatch?.[1] && String(leadMatch[1]).trim() === leadId;
  });
  const match = storedMatch || leadMatch || null;
  if (!match) return null;
  return {
    id: match?.id ?? null,
    bookingTime: String(match?.time || "").trim() || null,
    businessName: providerName,
  };
}

export function clearStaleTourScheduleState(lead: any): any {
  if (!lead) return lead;
  const extra = parseExtraLoose(lead?.extra) || {};
  const nextExtra = { ...extra };
  delete nextExtra.tourSchedule;

  const currentStatus = String(lead?.status || "").toUpperCase();
  const providerStatus = String(lead?.providerContact?.status || "").toUpperCase();
  const fallbackStatus =
    providerStatus === "UNLOCKED"
      ? "QUALIFYING"
      : currentStatus === "TOUR_SCHEDULED"
        ? "NEW"
        : lead?.status;

  return {
    ...lead,
    status: fallbackStatus,
    extra: nextExtra,
  };
}

export function getLatestTourCancellation(lead: any): {
  canceledAt?: string | null;
  canceledByName?: string | null;
  canceledByRole?: string | null;
  businessName?: string | null;
  bookingTime?: string | null;
} | null {
  const extra = parseExtraLoose(lead?.extra) || {};
  const cancellation = extra?.lastTourCancellation;
  if (!cancellation) return null;
  return {
    canceledAt: String(cancellation?.canceledAt || "").trim() || null,
    canceledByName: String(cancellation?.canceledByName || "").trim() || null,
    canceledByRole: String(cancellation?.canceledByRole || "").trim() || null,
    businessName:
      String(cancellation?.businessName || lead?.business?.name || "").trim() || null,
    bookingTime: String(cancellation?.bookingTime || "").trim() || null,
  };
}

export function getConciergePortalStatusMessage(lead: any): string {
  const extra = parseExtraLoose(lead?.extra) || {};
  const status = String(lead?.status || "").toUpperCase();
  const providerStatus = String(lead?.providerContact?.status || "").toUpperCase();
  const bookedTour = getTourScheduleFromLead(lead);
  const latestCancellation = getLatestTourCancellation(lead);

  if (bookedTour) {
    return getBookedTourMessage(lead, bookedTour);
  }

  if (status === "TOUR_SCHEDULED") {
    return `Your tour has already been scheduled with ${lead?.business?.name || "the provider"}. Use Change or Cancel Tour if you need to update it.`;
  }
  if (latestCancellation) {
    const providerName = latestCancellation.businessName || lead?.business?.name || "the provider";
    const whenLabel = formatTourScheduleLabel(latestCancellation.bookingTime);
    return whenLabel
      ? `Your scheduled tour for ${whenLabel} with ${providerName} was canceled. You can choose a new tour time when you're ready.`
      : `Your scheduled tour with ${providerName} was canceled. You can choose a new tour time when you're ready.`;
  }
  const customMessage = String(extra?.referrerStatusMessage || "").trim();
  if (customMessage) return customMessage;
  if (status === "PENDING_DECISION") {
    return "We are waiting on final decisions and will update you as soon as we have the next step.";
  }
  if (status === "PLACED" || status === "CLOSED") {
    return "Your referral is in the final placement stage. Our team will keep you updated on the final details.";
  }
  if (
    status === "ACCEPTED" ||
    providerStatus === "ACCEPTED" ||
    providerStatus === "UNLOCKED"
  ) {
    return "Accepted by care provider. Provider contact details are now available below.";
  }
  if (
    status === "SENT" ||
    status === "NO_RESPONSE" ||
    status === "NEW" ||
    status === "DRAFT" ||
    providerStatus === "SENT" ||
    providerStatus === "NO_RESPONSE" ||
    providerStatus === "NEW" ||
    providerStatus === "DRAFT" ||
    !providerStatus
  ) {
    return "Your request has been sent to matching providers. We will update you as providers respond.";
  }
  if (status === "DECLINED" || providerStatus === "DECLINED") {
    return "This provider declined the referral. We will keep tracking other matching options for you.";
  }
  if (
    status === "QUALIFYING" ||
    status === "MATCHING" ||
    providerStatus === "UNLOCKED"
  ) {
    return "We have contacted you and are helping coordinate the next step. If tour scheduling is opened for this provider, the action button will appear here.";
  }

  return "We are tracking this referral and will update you as soon as the provider responds.";
}

export function getLeadProviderContactInfo(lead: any): {
  email: string | null;
  phone: string | null;
} {
  const email =
    String(
      lead?.business?.emails?.[0] ??
        lead?.business?.email ??
        lead?.providerContact?.email ??
        "",
    ).trim() || null;
  const phone =
    String(
      lead?.business?.phoneNumbers?.[0]?.phoneNumber ??
        lead?.business?.phone ??
        lead?.providerContact?.phone ??
        "",
    ).trim() || null;
  return { email, phone };
}

export function buildLeadFromBroadcastInvite(inv: any): any {
  const seed = parseBroadcastSeed(inv?.group?.seedJson);
  return {
    id: `broadcast-${String(inv?.id ?? "")}`,
    status: String(inv?.status || "SENT"),
    createdAt: inv?.respondedAt || inv?.group?.createdAt || null,
    businessId: inv?.business?.id ?? null,
    business: {
      id: inv?.business?.id ?? null,
      name: inv?.business?.name ?? "Provider",
      url: inv?.business?.url ?? null,
      emails: inv?.business?.emails ?? null,
      phoneNumbers: inv?.business?.phoneNumbers ?? null,
    },
    group: {
      id: inv?.group?.id ?? null,
      selectedBusinessId:
        String(inv?.status || "").toUpperCase() === "ACCEPTED"
          ? inv?.business?.id ?? null
          : null,
    },
    providerContact: {
      status: String(inv?.status || "SENT").toUpperCase(),
      email: inv?.business?.emails?.[0] ?? null,
      phone: inv?.business?.phoneNumbers?.[0]?.phoneNumber ?? null,
    },
    providerNote: inv?.providerNote ?? null,
    referrerContact: seed.referrerContact ?? null,
    residentKey: seed.residentInitials ?? null,
    careNeed: seed.careNeed ?? null,
    payerType: seed.payerType ?? null,
    urgency: seed.urgency ?? null,
    extra: {
      patientInitials: seed.residentInitials ?? null,
      careNeed: seed.careNeed ?? null,
      payerType: seed.payerType ?? null,
      notes: seed.notes ?? null,
      providerNote: inv?.providerNote ?? null,
      referrerStatusMessage: seed.referrerStatusMessage ?? null,
      referrerContact: seed.referrerContact ?? null,
    },
  };
}
